import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { servicesAPI, reviewsAPI } from '../api'
import {
  RiCalendarCheckLine, RiScissorsFill, RiStarFill,
  RiArrowRightLine, RiCheckLine, RiSparklingLine,
  RiShieldCheckLine, RiAwardFill, RiHeartFill,
  RiPaintBrushFill, RiContrastDrop2Fill, RiLeafFill,
} from 'react-icons/ri'
import SEO from '../components/seo/SEO'
import { breadcrumb, localBusinessReview } from '../components/seo/jsonLd'

/* ─── Helpers ──────────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } }, { threshold })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

function AnimSection({ children, className = '' }) {
  const [ref, visible] = useInView()
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}>
      {children}
    </div>
  )
}

/* ─── Hero ─────────────────────────────────────────────────── */
function HeroSection() {
  return (
    <section className="relative min-h-[100vh] flex items-center overflow-hidden bg-salon-dark">
      {/* Banner photo (right-weighted; text reads on the dark left side) */}
      <div className="absolute inset-0">
        <img
          src="/brand/banner.png"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-right"
          fetchpriority="high"
        />
        {/* Left-to-right gradient overlay so text remains legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-salon-dark via-salon-dark/85 to-salon-dark/10" />
        {/* Decorative gold/rose blobs */}
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 left-1/3 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #C9963A 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      </div>

      <div className="container-salon w-full relative z-10 pt-28 pb-24">
        <div className="max-w-2xl">
          {/* Eyebrow tagline (matches the banner-with-text artwork) */}
          <p className="text-salon-secondary tracking-[0.4em] text-xs sm:text-sm font-semibold font-body mb-5 animate-fade-up">
            BEAUTY <span className="text-salon-secondary/50 mx-2">•</span> STYLE <span className="text-salon-secondary/50 mx-2">•</span> CONFIDENCE
          </p>

          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-2 animate-fade-up"
              style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
            Your Beauty,
          </h1>
          <p
            className="text-salon-secondary text-5xl sm:text-6xl lg:text-7xl leading-[1.1] mb-6 animate-fade-up italic"
            style={{ fontFamily: '"Great Vibes", "Playfair Display", cursive', animationDelay: '0.15s', animationFillMode: 'both' }}
          >
            Our Passion
          </p>

          <div className="h-[2px] w-28 bg-gradient-to-r from-salon-secondary to-transparent mb-7 animate-fade-up"
               style={{ animationDelay: '0.2s', animationFillMode: 'both' }} />

          <p className="text-white/80 font-body text-lg lg:text-xl leading-relaxed max-w-xl mb-10 animate-fade-up"
             style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
            Premium salon services crafted to bring out your best self.
            Hair, skin, nails, makeup &amp; wellness — everything you need, all in one place.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12 animate-fade-up"
               style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
            <Link to="/book" className="btn-gold text-base px-8 py-4 shadow-gold">
              <RiCalendarCheckLine className="text-xl" /> Book Your Appointment
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 text-white/85 hover:text-white font-semibold font-body px-2">
              Explore Services <RiArrowRightLine />
            </Link>
          </div>

          {/* Brand pillars (matches banner: BOOK / EXPERT / PREMIUM / HYGIENE) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl animate-fade-up"
               style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
            {[
              { icon: RiCalendarCheckLine, title: 'BOOK',     sub: 'Online, anytime' },
              { icon: RiAwardFill,         title: 'EXPERT',   sub: 'Master stylists' },
              { icon: RiSparklingLine,     title: 'PREMIUM',  sub: 'Luxury products' },
              { icon: RiShieldCheckLine,   title: 'HYGIENE',  sub: 'Sanitised space' },
            ].map((p) => (
              <div key={p.title} className="flex flex-col items-start">
                <div className="w-10 h-10 rounded-xl bg-salon-secondary/15 border border-salon-secondary/30
                                flex items-center justify-center mb-2">
                  <p.icon className="text-salon-secondary text-lg" />
                </div>
                <p className="text-white font-bold text-xs tracking-widest font-body">{p.title}</p>
                <p className="text-white/50 text-[11px] font-body mt-0.5">{p.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom service ribbon — matches the lower bar in the brand banner */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-r from-salon-primary-dark via-salon-primary to-salon-primary-dark
                      border-t border-salon-secondary/30">
        <div className="container-salon py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-white font-body font-semibold tracking-[0.25em] text-xs sm:text-sm">
            HAIR <span className="text-salon-secondary mx-1.5">•</span> SKIN <span className="text-salon-secondary mx-1.5">•</span>
            NAILS <span className="text-salon-secondary mx-1.5">•</span> MAKEUP <span className="text-salon-secondary mx-1.5">•</span> WELLNESS
          </p>
          <p className="text-salon-secondary font-body italic text-xs sm:text-sm">
            Everything You Need. All in One Place.
          </p>
        </div>
      </div>
    </section>
  )
}

/* ─── Trust strip ──────────────────────────────────────────── */
function TrustStrip() {
  const stats = [
    { v: '5,000+', l: 'Happy Clients' },
    { v: '15+',    l: 'Master Stylists' },
    { v: '4.9★',   l: 'Avg. Rating' },
    { v: '8 yrs',  l: 'Of Excellence' },
  ]
  return (
    <section className="py-10 bg-white border-b border-salon-cream-dark">
      <div className="container-salon grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s) => (
          <div key={s.l} className="text-center">
            <p className="font-display text-salon-primary text-3xl md:text-4xl font-bold">{s.v}</p>
            <p className="text-salon-muted text-xs md:text-sm font-body mt-1 tracking-wide uppercase">{s.l}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

/* ─── Service categories (vibrant) ─────────────────────────── */
function CategoriesSection() {
  const categories = [
    { name: 'Hair',     desc: 'Cuts, colour, treatments, styling',     icon: RiScissorsFill,    tone: 'from-salon-primary to-salon-primary-light' },
    { name: 'Skin',     desc: 'Facials, peels, glow rituals',          icon: RiContrastDrop2Fill, tone: 'from-salon-primary-light to-salon-accent' },
    { name: 'Nails',    desc: 'Manicures, pedicures, gel & art',       icon: RiPaintBrushFill,  tone: 'from-salon-accent to-salon-secondary' },
    { name: 'Makeup',   desc: 'Bridal, party, editorial looks',         icon: RiHeartFill,       tone: 'from-salon-secondary to-salon-secondary-light' },
    { name: 'Wellness', desc: 'Massage, head spa, relaxation',          icon: RiLeafFill,        tone: 'from-salon-primary to-salon-secondary' },
  ]
  return (
    <section className="py-20 bg-cream-gradient">
      <div className="container-salon">
        <AnimSection className="text-center mb-12">
          <span className="section-tag">All Beauty, One Roof</span>
          <h2 className="section-title mt-2">Five Pillars of Salon Vora</h2>
          <p className="text-salon-muted font-body mt-4 max-w-xl mx-auto">
            Choose any pillar — or combine them. Our stylists craft each session around you.
          </p>
        </AnimSection>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {categories.map((c) => (
            <AnimSection key={c.name}>
              <Link
                to="/services"
                className={`block rounded-3xl p-6 h-full bg-gradient-to-br ${c.tone}
                            text-white relative overflow-hidden group hover:-translate-y-1.5 hover:shadow-salon-lg
                            transition-all duration-300`}
              >
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl group-hover:scale-125 transition-transform" />
                <c.icon className="text-3xl mb-4 relative z-10" />
                <p className="font-display font-bold text-2xl mb-1 relative z-10">{c.name}</p>
                <p className="text-white/80 text-xs font-body relative z-10">{c.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold relative z-10 group-hover:gap-2 transition-all">
                  Explore <RiArrowRightLine />
                </div>
              </Link>
            </AnimSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Services Preview ─────────────────────────────────────── */
function ServicesSection() {
  const { data } = useQuery({
    queryKey: ['services-preview'],
    queryFn: () => servicesAPI.getAll({ per_page: 6, featured: true }),
    select: (r) => r.data?.data || [],
  })

  const services = data?.length ? data : FALLBACK_SERVICES

  return (
    <section className="py-20 bg-white">
      <div className="container-salon">
        <AnimSection className="text-center mb-14">
          <span className="section-tag">Featured Services</span>
          <h2 className="section-title mt-2">Treatments Made For You</h2>
          <p className="text-salon-muted font-body mt-4 max-w-xl mx-auto">
            From classic cuts to avant-garde colour — our menu covers every facet of beauty.
          </p>
        </AnimSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <AnimSection key={s.id || i}>
              <Link to={`/services/${s.slug || s.id}`} className="card group block p-6">
                <div className="w-12 h-12 rounded-2xl bg-salon-primary/10 flex items-center justify-center mb-4
                                group-hover:bg-salon-primary transition-colors duration-300">
                  <RiScissorsFill className="text-salon-primary group-hover:text-white text-xl transition-colors duration-300" />
                </div>
                <h3 className="font-display text-salon-dark text-lg font-semibold mb-2">{s.name}</h3>
                <p className="text-salon-muted font-body text-sm leading-relaxed mb-4 line-clamp-2">
                  {s.description || 'Professional service tailored to your needs.'}
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-salon-secondary font-bold font-body text-lg">₹{parseFloat(s.price || 0).toLocaleString()}</p>
                    <p className="text-salon-muted text-xs font-body">{s.duration_minutes || 60} min</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-body text-salon-primary font-medium
                                  group-hover:gap-2 transition-all">
                    Book <RiArrowRightLine />
                  </div>
                </div>
              </Link>
            </AnimSection>
          ))}
        </div>

        <AnimSection className="text-center mt-10">
          <Link to="/services" className="btn-secondary">
            View All Services <RiArrowRightLine />
          </Link>
        </AnimSection>
      </div>
    </section>
  )
}

/* ─── Why Us ───────────────────────────────────────────────── */
function WhyUsSection() {
  const perks = [
    { icon: '✨', title: 'Premium Products', desc: 'We use only top-tier, salon-grade products from global brands.' },
    { icon: '👩‍🎨', title: 'Expert Stylists',  desc: 'Our team has 10+ years of collective experience in luxury styling.' },
    { icon: '🕐', title: 'Flexible Timing',   desc: 'Open 7 days a week with morning and evening slots available.' },
    { icon: '📱', title: 'Easy Online Booking', desc: 'Book, reschedule, or cancel appointments effortlessly online.' },
    { icon: '💳', title: 'Transparent Pricing', desc: 'No hidden charges. What you see is exactly what you pay.' },
    { icon: '⭐', title: '5-Star Experience',  desc: 'Rated 4.9/5 by thousands of satisfied clients across Chennai.' },
  ]

  return (
    <section className="py-20 bg-salon-cream">
      <div className="container-salon">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <AnimSection>
            <span className="section-tag">Why Choose Us</span>
            <h2 className="section-title mt-2 mb-4">The Salon Vora Difference</h2>
            <div className="divider-gold mb-6" />
            <p className="text-salon-muted font-body leading-relaxed mb-8">
              We believe beauty is an art form. Every client who walks through our doors deserves a personalised, luxury experience — whether it's a quick trim or a full bridal package.
            </p>
            <Link to="/about" className="btn-primary">
              Learn Our Story <RiArrowRightLine />
            </Link>
          </AnimSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {perks.map((p) => (
              <AnimSection key={p.title}>
                <div className="p-5 rounded-2xl bg-white border border-salon-cream-dark hover:border-salon-primary/30
                                hover:shadow-salon-sm transition-all duration-300">
                  <div className="text-2xl mb-3">{p.icon}</div>
                  <p className="font-semibold font-body text-salon-dark mb-1">{p.title}</p>
                  <p className="text-salon-muted font-body text-sm leading-relaxed">{p.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── How it works ─────────────────────────────────────────── */
function HowItWorksSection() {
  const steps = [
    { n: 1, title: 'Browse & Choose',  desc: 'Explore our menu of services and pick what suits you.' },
    { n: 2, title: 'Pick Your Stylist', desc: 'Select an expert based on speciality, ratings, or availability.' },
    { n: 3, title: 'Lock Your Slot',    desc: 'Choose a date and time that works for you, in seconds.' },
    { n: 4, title: 'Glow Up',           desc: 'Walk in, relax, and let our team work their magic.' },
  ]
  return (
    <section className="py-20 bg-white">
      <div className="container-salon">
        <AnimSection className="text-center mb-14">
          <span className="section-tag">How It Works</span>
          <h2 className="section-title mt-2">Book in Under Two Minutes</h2>
        </AnimSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          <div aria-hidden="true" className="hidden lg:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-salon-primary/30 to-transparent" />
          {steps.map((s) => (
            <AnimSection key={s.n}>
              <div className="text-center px-2 relative">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-salon-primary text-white font-display
                                font-bold text-xl flex items-center justify-center shadow-salon-md ring-4 ring-white">
                  {s.n}
                </div>
                <p className="font-display font-semibold text-salon-dark text-lg mb-1">{s.title}</p>
                <p className="text-salon-muted font-body text-sm">{s.desc}</p>
              </div>
            </AnimSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Reviews ──────────────────────────────────────────────── */
function ReviewsSection() {
  const { data } = useQuery({
    queryKey: ['reviews-home'],
    queryFn: () => reviewsAPI.getAll({ per_page: 6, featured: true }),
    select: (r) => r.data?.data || [],
  })
  const reviews = data?.length ? data : FALLBACK_REVIEWS

  return (
    <section className="py-20 bg-hero-gradient">
      <div className="container-salon">
        <AnimSection className="text-center mb-14">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-salon-secondary px-3 py-1 rounded-full bg-salon-secondary/10 mb-3">
            Client Love
          </span>
          <h2 className="section-title-light mt-2">What Our Clients Say</h2>
        </AnimSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <AnimSection key={r.id || i}>
              <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <RiStarFill key={j} className={j < (r.rating || 5) ? 'text-salon-secondary' : 'text-white/20'} />
                  ))}
                </div>
                <p className="text-white/80 font-body text-sm leading-relaxed mb-5 italic">
                  "{r.comment || r.text || 'Absolutely amazing experience!'}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-salon-primary flex items-center justify-center text-white text-sm font-bold">
                    {(r.user?.name || r.name || 'A').charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm font-body">{r.user?.name || r.name}</p>
                    <p className="text-white/50 text-xs font-body">{r.service?.name || 'Hair Service'}</p>
                  </div>
                </div>
              </div>
            </AnimSection>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── CTA ──────────────────────────────────────────────────── */
function CTASection() {
  return (
    <section className="py-20 bg-salon-cream">
      <div className="container-salon">
        <AnimSection>
          <div className="bg-salon-primary rounded-3xl px-8 py-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-salon-secondary/20 blur-3xl" />
            </div>
            <div className="relative z-10">
              <p className="font-display text-white text-4xl lg:text-5xl font-bold mb-4">
                Ready for your glow-up?
              </p>
              <p className="text-white/70 font-body text-lg mb-8 max-w-lg mx-auto">
                Slots fill up fast. Book today and secure your preferred stylist and time.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/book" className="btn-gold text-base px-10 py-4">
                  <RiCalendarCheckLine className="text-xl" /> Book Your Slot Now
                </Link>
                <a href="tel:+919876543210" className="btn-secondary text-base px-8 py-4 border-white text-white hover:bg-white hover:text-salon-primary">
                  Call +91 98765 43210
                </a>
              </div>
            </div>
          </div>
        </AnimSection>
      </div>
    </section>
  )
}

/* ─── Fallback data ─────────────────────────────────────────── */
const FALLBACK_SERVICES = [
  { id: 1, name: 'Hair Coloring',     price: 2500, duration_minutes: 120, slug: 'hair-coloring',   description: 'Full color, highlights, balayage & more.' },
  { id: 2, name: 'Haircut & Style',   price: 800,  duration_minutes: 60,  slug: 'haircut-style',   description: 'Precision cuts tailored to your face shape.' },
  { id: 3, name: 'Bridal Makeup',     price: 8000, duration_minutes: 180, slug: 'bridal-makeup',   description: 'Complete bridal package for your big day.' },
  { id: 4, name: 'Deep Conditioning', price: 1200, duration_minutes: 90,  slug: 'deep-conditioning', description: 'Restore moisture and shine to your hair.' },
  { id: 5, name: 'Facial Treatment',  price: 1500, duration_minutes: 60,  slug: 'facial',          description: 'Customised facial for radiant, glowing skin.' },
  { id: 6, name: 'Nail Art',          price: 600,  duration_minutes: 45,  slug: 'nail-art',        description: 'Intricate nail designs by certified artists.' },
]
const FALLBACK_REVIEWS = [
  { id: 1, rating: 5, comment: 'Best salon in Chennai! The stylists are so talented and the ambience is just gorgeous.', name: 'Priya Sharma' },
  { id: 2, rating: 5, comment: 'I got my bridal makeup done here and felt absolutely stunning. Highly recommended!', name: 'Ananya Krishnan' },
  { id: 3, rating: 5, comment: 'Love the online booking system – so easy. My hair color turned out perfect!', name: 'Deepika Nair' },
  { id: 4, rating: 4, comment: 'Friendly staff, excellent service, and great pricing. Will definitely come back.', name: 'Meena Pillai' },
  { id: 5, rating: 5, comment: 'The facial here is out of this world. My skin was glowing for weeks after.', name: 'Roshni Thomas' },
  { id: 6, rating: 5, comment: 'Finally found my go-to salon. The balayage was done exactly as I wanted!', name: 'Kavitha Reddy' },
]

/* ─── Page ─────────────────────────────────────────────────── */
export default function Home() {
  return (
    <>
      <SEO
        title="Luxury Hair, Skin, Nails & Bridal Studio in Chennai"
        description="Salon Vora — Chennai's premium beauty destination. Hair, skin, nails, makeup and wellness, all under one roof. Book online today."
        path="/"
        jsonLd={[
          breadcrumb([{ name: 'Home', url: '/' }]),
          localBusinessReview({ rating: 4.9, count: 1200 }),
        ]}
      />
      <HeroSection />
      <TrustStrip />
      <CategoriesSection />
      <ServicesSection />
      <WhyUsSection />
      <HowItWorksSection />
      <ReviewsSection />
      <CTASection />
    </>
  )
}
